import { useEffect, useMemo, useState } from "react";
import {
  FaAward,
  FaCalendarAlt,
  FaChartLine,
  FaClock,
  FaCog,
  FaEdit,
  FaEye,
  FaSearch,
  FaStar,
  FaSyncAlt,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import client from "../api/client";
import "./EmployeePerformance.css";

const getCurrentMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

const buildMonthOptions = () => {
  const options = [];
  const cursor = new Date();
  cursor.setDate(1);

  for (let index = 0; index < 12; index += 1) {
    const value = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      value,
      label: cursor.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      }),
    });
    cursor.setMonth(cursor.getMonth() - 1);
  }

  return options;
};

const formatScore = (value) => `${Number(value || 0).toFixed(0)}/100`;

const formatHours = (value) => {
  const hours = Number(value || 0);
  if (!hours) return "0 hrs";
  if (hours < 1) return "< 1 hr";
  return `${hours.toFixed(hours % 1 === 0 ? 0 : 1)} hrs`;
};

const getStatusClass = (status) =>
  String(status || "Needs Improvement")
    .toLowerCase()
    .replace(/\s+/g, "-");

export default function EmployeePerformance() {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState({ attendance: 40, completion: 40, feedback: 20 });
  const [showSettings, setShowSettings] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null); // null or employee object
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [isAdmin, setIsAdmin] = useState(true); // for now assume admin, can be connected to auth later

  const monthOptions = useMemo(buildMonthOptions, []);
  const currentMonth = getCurrentMonth();

  // Calculate total weight for validation
  const totalWeight = useMemo(() => {
    return Number(settings.attendance) + Number(settings.completion) + Number(settings.feedback);
  }, [settings]);

  const loadPerformance = async (month = selectedMonth) => {
    setLoading(true);
    setError("");

    try {
      const res = await client.get("/performance", { params: { month } });
      const rows = Array.isArray(res.data?.employees) ? res.data.employees : [];
      setEmployees(rows);
      if (res.data.settings) {
        setSettings(res.data.settings);
      }
      setSelectedEmployee((active) => {
        if (!active) return null;
        return rows.find((row) => row.employeeId === active.employeeId) || null;
      });
    } catch (err) {
      console.error("Load employee performance failed:", err);
      setEmployees([]);
      setSelectedEmployee(null);
      setError("Unable to load employee performance right now.");
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount
  const loadSettings = async () => {
    try {
      const res = await client.get("/performance/settings");
      setSettings(res.data);
    } catch (err) {
      console.error("Load performance settings failed:", err);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSettingsError("");
    setSaveSuccess("");
    if (totalWeight !== 100) {
      setSettingsError(`Total weightage must be 100%. Current total: ${totalWeight}%`);
      return;
    }
    setSettingsLoading(true);
    try {
      await client.post("/performance/settings", settings);
      setSaveSuccess("Settings saved successfully!");
      setTimeout(() => setSaveSuccess(""), 3000);
      // Reload performance to apply new weights
      await loadPerformance(selectedMonth);
    } catch (err) {
      setSettingsError(err.response?.data?.message || "Failed to save settings");
      console.error("Save settings failed:", err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const openEditModal = (employee) => {
    setShowEditModal(employee);
    setEditForm({
      manualScore: employee.manualScore ?? "",
      manualRank: employee.manualRank ?? "",
      remarks: employee.remarks ?? "",
    });
  };

  const handleSaveOverride = async () => {
    setEditSaving(true);
    try {
      await client.post("/performance/override", {
        employeeId: showEditModal.employeeId,
        month: selectedMonth,
        manualScore: editForm.manualScore !== "" ? Number(editForm.manualScore) : null,
        manualRank: editForm.manualRank !== "" ? Number(editForm.manualRank) : null,
        remarks: editForm.remarks,
      });
      setShowEditModal(null);
      await loadPerformance(selectedMonth);
    } catch (err) {
      console.error("Failed to save override", err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetOverride = async () => {
    setEditSaving(true);
    try {
      await client.post("/performance/override/reset", {
        employeeId: showEditModal.employeeId,
        month: selectedMonth,
      });
      setShowEditModal(null);
      await loadPerformance(selectedMonth);
    } catch (err) {
      console.error("Failed to reset override", err);
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    loadPerformance(selectedMonth);
    loadSettings();
  }, [selectedMonth]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;

    return employees.filter((employee) =>
      String(employee.employeeName || "").toLowerCase().includes(term)
    );
  }, [employees, search]);

  const summary = useMemo(() => {
    const totalEmployees = employees.length;
    const averageScore = totalEmployees
      ? employees.reduce((sum, employee) => sum + Number(employee.performanceScore || 0), 0) /
        totalEmployees
      : 0;
    const topPerformer = employees[0]?.employeeName || "-";
    const completedTasks = employees.reduce(
      (sum, employee) => sum + Number(employee.taskMetrics?.completed || 0),
      0
    );
    const totalFeedback = employees.reduce(
      (sum, employee) => sum + Number(employee.feedbackMetrics?.totalFeedback || 0),
      0
    );
    const averageRating = employees.length
      ? employees.reduce((sum, employee) => sum + Number(employee.feedbackMetrics?.averageRating || 0), 0) /
        employees.length
      : 0;

    return {
      totalEmployees,
      averageScore,
      topPerformer,
      completedTasks,
      totalFeedback,
      averageRating,
    };
  }, [employees]);

  return (
    <div className="employee-performance-page">
      <header className="employee-performance-header">
        <div className="employee-performance-title-block">
          <div className="employee-performance-logo">
            <FaAward />
          </div>
          <div>
            <h1>Employee Performance</h1>
            <p>Dynamic rankings from attendance and task activity.</p>
          </div>
        </div>

        <div className="employee-performance-actions">
          <label className="performance-month-filter">
            <FaCalendarAlt />
            <select
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              aria-label="Select month"
            >
              {monthOptions.map((option, index) => (
                <option key={option.value} value={option.value}>
                  {index === 0 ? `Current Month - ${option.label}` : option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="performance-icon-button"
            onClick={() => loadPerformance(selectedMonth)}
            disabled={loading}
            title="Refresh performance"
          >
            <FaSyncAlt />
          </button>
          <button
            type="button"
            className="performance-icon-button"
            onClick={() => setShowSettings(!showSettings)}
            title="Performance Matrix Settings"
          >
            <FaCog />
          </button>
        </div>
      </header>

      {/* Performance Matrix Settings Section */}
      {showSettings && (
        <section className="performance-settings-section">
          <div className="performance-settings-card">
            <h2><FaCog /> Performance Matrix Settings</h2>
            <p className="settings-description">Configure weightages for performance metrics. Total must equal 100%.</p>
            
            {settingsError && <div className="settings-message error">{settingsError}</div>}
            {saveSuccess && <div className="settings-message success">{saveSuccess}</div>}
            
            <div className="settings-inputs">
              <div className="settings-input-group">
                <label htmlFor="attendance-weight">Attendance Weightage</label>
                <div className="input-with-percent">
                  <input
                    id="attendance-weight"
                    type="number"
                    min="0"
                    max="100"
                    value={settings.attendance}
                    onChange={(e) => setSettings(s => ({ ...s, attendance: Number(e.target.value || 0) }))}
                  />
                  <span>%</span>
                </div>
              </div>

              <div className="settings-input-group">
                <label htmlFor="completion-weight">Task Completion Weightage</label>
                <div className="input-with-percent">
                  <input
                    id="completion-weight"
                    type="number"
                    min="0"
                    max="100"
                    value={settings.completion}
                    onChange={(e) => setSettings(s => ({ ...s, completion: Number(e.target.value || 0) }))}
                  />
                  <span>%</span>
                </div>
              </div>

              <div className="settings-input-group">
                <label htmlFor="feedback-weight">Team Feedback Weightage</label>
                <div className="input-with-percent">
                  <input
                    id="feedback-weight"
                    type="number"
                    min="0"
                    max="100"
                    value={settings.feedback}
                    onChange={(e) => setSettings(s => ({ ...s, feedback: Number(e.target.value || 0) }))}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            <div className="settings-total">
              <span>Total:</span>
              <span className={`total-value ${totalWeight !== 100 ? "error" : "success"}`}>
                {totalWeight}%
              </span>
            </div>

            <button
              type="button"
              className="save-settings-button"
              onClick={handleSaveSettings}
              disabled={settingsLoading}
            >
              {settingsLoading ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </section>
      )}

      <div className="performance-period-tabs" aria-label="Performance period">
        <button
          type="button"
          className={selectedMonth === currentMonth ? "active" : ""}
          onClick={() => setSelectedMonth(currentMonth)}
        >
          Current Month
        </button>
        <button
          type="button"
          className={selectedMonth !== currentMonth ? "active" : ""}
          onClick={() => {
            const previousMonth = monthOptions[1]?.value || currentMonth;
            setSelectedMonth(previousMonth);
          }}
        >
          Previous Months
        </button>
      </div>

      <section className="performance-summary-grid">
        <article className="performance-summary-card">
          <span>Total Employees</span>
          <strong>{summary.totalEmployees}</strong>
        </article>
        <article className="performance-summary-card">
          <span>Average Score</span>
          <strong>{formatScore(summary.averageScore)}</strong>
        </article>
        <article className="performance-summary-card">
          <span>Top Performer</span>
          <strong>{summary.topPerformer}</strong>
        </article>
        <article className="performance-summary-card">
          <span>Completed Tasks</span>
          <strong>{summary.completedTasks}</strong>
        </article>
        <article className="performance-summary-card">
          <span>Total Feedback</span>
          <strong>{summary.totalFeedback}</strong>
        </article>
        <article className="performance-summary-card">
          <span>Average Team Rating</span>
          <strong>{summary.averageRating > 0 ? `${summary.averageRating.toFixed(1)}/5` : "N/A"}</strong>
        </article>
      </section>

      <section className="performance-table-section">
        <div className="performance-table-toolbar">
          <div>
            <h2>Ranking</h2>
            <p>Sorted automatically by highest performance score.</p>
          </div>
          <label className="performance-search">
            <FaSearch />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search employee"
            />
          </label>
        </div>

        {error ? <div className="performance-message error">{error}</div> : null}

        <div className="performance-table-wrap">
          <table className="performance-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Employee Name</th>
                <th>Performance Score</th>
                <th>Attendance</th>
                <th>Task</th>
                <th>Team Rating</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="performance-empty">
                    Loading performance data...
                  </td>
                </tr>
              ) : filteredEmployees.length ? (
                filteredEmployees.map((employee) => (
                  <tr key={employee.employeeId} className={employee.isOverridden ? "overridden" : ""}>
                    <td>
                      <span className="performance-rank">
                        #{employee.rank || 0}
                        {employee.manualRank && <small className="manual-badge">(Manual)</small>}
                      </span>
                    </td>
                    <td>
                      <div className="performance-employee-cell">
                        <span>{employee.employeeName || "-"}</span>
                        {employee.role ? <small>{employee.role}</small> : null}
                      </div>
                    </td>
                    <td>
                      <div className="performance-score-cell">
                        <strong>{formatScore(employee.performanceScore)}</strong>
                        {employee.isOverridden && (
                          <small className="auto-score-badge">
                            Auto: {formatScore(employee.autoScore)}
                          </small>
                        )}
                        <span>
                          <i style={{ width: `${Number(employee.performanceScore || 0)}%` }} />
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="performance-component-cell">
                        <strong>{employee.componentScores?.attendance || 0}/100</strong>
                      </div>
                    </td>
                    <td>
                      <div className="performance-component-cell">
                        <strong>{employee.componentScores?.task || 0}/100</strong>
                      </div>
                    </td>
                    <td>
                      <div className="performance-rating-cell">
                        {employee.feedbackMetrics?.totalFeedback > 0 ? (
                          <>
                            <div className="star-rating">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  className={star <= Math.round(employee.feedbackMetrics.averageRating) ? "filled" : ""}
                                />
                              ))}
                            </div>
                            <span>
                              {employee.feedbackMetrics.averageRating.toFixed(1)}/5 | {employee.feedbackMetrics.totalFeedback} feedback
                            </span>
                          </>
                        ) : (
                          <span className="no-rating">Not rated yet</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`performance-status ${getStatusClass(employee.status)}`}>
                        {employee.status || "Needs Improvement"}
                      </span>
                    </td>
                    <td>
                      <div className="performance-actions">
                        <button
                          type="button"
                          className="performance-view-button"
                          onClick={() => setSelectedEmployee(employee)}
                        >
                          <FaEye />
                          View
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            className="performance-edit-button"
                            onClick={() => openEditModal(employee)}
                          >
                            <FaEdit />
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="performance-empty">
                    No employee performance data found for this month.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedEmployee ? (
        <div className="performance-detail-overlay" role="presentation">
          <aside className="performance-detail-panel" aria-label="Employee performance details">
            <div className="performance-detail-header">
              <div>
                <span>Rank #{selectedEmployee.rank || 0}</span>
                <h2>{selectedEmployee.employeeName || "-"}</h2>
              </div>
              <button
                type="button"
                className="performance-close-button"
                onClick={() => setSelectedEmployee(null)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="performance-current-score">
              <FaChartLine />
              <div>
                <span>Current Performance Score</span>
                <strong>{formatScore(selectedEmployee.performanceScore)}</strong>
              </div>
            </div>

            <div className="performance-detail-group">
              <h3>
                <FaUserCheck />
                Attendance Metrics
              </h3>
              <div className="performance-metric-list">
                <span>Present Days</span>
                <strong>{selectedEmployee.attendanceMetrics?.presentDays || 0}</strong>
                <span>Absent Days</span>
                <strong>{selectedEmployee.attendanceMetrics?.absentDays || 0}</strong>
                <span>Late Days</span>
                <strong>{selectedEmployee.attendanceMetrics?.lateDays || 0}</strong>
                <span>Attendance Score</span>
                <strong>{selectedEmployee.componentScores?.attendance || 0}/100</strong>
              </div>
            </div>

            <div className="performance-detail-group">
              <h3>
                <FaAward />
                Task Metrics
              </h3>
              <div className="performance-metric-list">
                <span>Total Assigned</span>
                <strong>{selectedEmployee.taskMetrics?.totalAssigned || 0}</strong>
                <span>Completed</span>
                <strong>{selectedEmployee.taskMetrics?.completed || 0}</strong>
                <span>Pending</span>
                <strong>{selectedEmployee.taskMetrics?.pending || 0}</strong>
                <span>Completion Percentage</span>
                <strong>{selectedEmployee.taskMetrics?.completionPercentage || 0}%</strong>
              </div>
            </div>

            <div className="performance-detail-group">
              <h3>
                <FaAward />
                Task Metrics
              </h3>
              <div className="performance-metric-list">
                <span>Total Assigned</span>
                <strong>{selectedEmployee.taskMetrics?.totalAssigned || 0}</strong>
                <span>Completed</span>
                <strong>{selectedEmployee.taskMetrics?.completed || 0}</strong>
                <span>Pending</span>
                <strong>{selectedEmployee.taskMetrics?.pending || 0}</strong>
                <span>Completion Percentage</span>
                <strong>{selectedEmployee.taskMetrics?.completionPercentage || 0}%</strong>
                <span>Task Score</span>
                <strong>{selectedEmployee.componentScores?.task || 0}/100</strong>
              </div>
            </div>

            <div className="performance-detail-group">
              <h3>
                <FaStar />
                Team Feedback Metrics
              </h3>
              <div className="performance-metric-list">
                <span>Total Feedback Received</span>
                <strong>{selectedEmployee.feedbackMetrics?.totalFeedback || 0}</strong>
                <span>Average Rating</span>
                <strong>
                  {selectedEmployee.feedbackMetrics?.averageRating > 0
                    ? `${selectedEmployee.feedbackMetrics.averageRating.toFixed(1)}/5`
                    : "Not rated yet"}
                </strong>
                <span>Feedback Score</span>
                <strong>{selectedEmployee.componentScores?.feedback || 0}/100</strong>
                <span>Positive Feedback (4-5★)</span>
                <strong>{selectedEmployee.feedbackMetrics?.positiveCount || 0}</strong>
                <span>Neutral Feedback (3★)</span>
                <strong>{selectedEmployee.feedbackMetrics?.neutralCount || 0}</strong>
                <span>Negative Feedback (1-2★)</span>
                <strong>{selectedEmployee.feedbackMetrics?.negativeCount || 0}</strong>
              </div>
            </div>

            <div className="performance-detail-group">
              <h3>
                <FaChartLine />
                Performance Summary
              </h3>
              <div className="performance-metric-list">
                <span>Current Performance Score</span>
                <strong>
                  {formatScore(selectedEmployee.efficiencyMetrics?.currentPerformanceScore)}
                </strong>
                <span>Current Rank</span>
                <strong>#{selectedEmployee.efficiencyMetrics?.currentRank || 0}</strong>
              </div>
            </div>
          </aside>
        </div>
      ) : null}

      {showEditModal ? (
        <div className="performance-edit-overlay" onClick={() => setShowEditModal(null)}>
          <div className="performance-edit-panel" onClick={(e) => e.stopPropagation()}>
            <div className="performance-edit-header">
              <h2>Edit Performance Override</h2>
              <button
                type="button"
                onClick={() => setShowEditModal(null)}
                className="performance-close-button"
              >
                <FaTimes />
              </button>
            </div>

            <div className="performance-edit-content">
              <h3 style={{ marginBottom: "16px" }}>{showEditModal.employeeName}</h3>

              <div className="performance-edit-field">
                <label>
                  Manual Score (0-100, leave blank for auto)
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editForm.manualScore}
                    onChange={(e) => setEditForm({ ...editForm, manualScore: e.target.value })}
                    placeholder={`Auto: ${showEditModal.autoScore}`}
                  />
                </label>
              </div>

              <div className="performance-edit-field">
                <label>
                  Manual Rank (leave blank for auto)
                  <input
                    type="number"
                    min="1"
                    value={editForm.manualRank}
                    onChange={(e) => setEditForm({ ...editForm, manualRank: e.target.value })}
                  />
                </label>
              </div>

              <div className="performance-edit-field">
                <label>
                  Remarks (optional)
                  <textarea
                    value={editForm.remarks}
                    onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                    rows="3"
                  />
                </label>
              </div>

              <div className="performance-edit-actions">
                {showEditModal.isOverridden && (
                  <button
                    type="button"
                    className="performance-reset-button"
                    onClick={handleResetOverride}
                    disabled={editSaving}
                  >
                    Reset to Auto
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowEditModal(null)}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="performance-save-button"
                  onClick={handleSaveOverride}
                  disabled={editSaving}
                >
                  {editSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
